/**
 * PieceSetup Mini-Game
 *
 * Self-contained game: Engine + Definition + Registration
 *
 * Rules:
 * - Starting board is the FEN from the PGN [FEN] header (default: empty board).
 * - Target position is standard starting position (or params.targetFen).
 * - Player places/moves pieces freely to match the target.
 * - Win: Board matches the target position (or specified piece types match).
 */
import { MiniGameBase } from "../MiniGameBase";
import { MiniGameRegistry } from "../MiniGameRegistry";
import * as BoardUtils from "../BoardUtils";
import { CHESS_START_POSITION, CHESS_EMPTY_BOARD_FEN } from "../../constants";
// ========================================
// Engine
// ========================================
export class PieceSetupEngine extends MiniGameBase {
    playerColorFilter;
    matchTypes;
    constructor(config) {
        super(config);
        // Field declarations with `!` still emit initializers that overwrite values
        // set during initializeState() (called by super). Re-assign here.
        const params = config.params || {};
        this.playerColorFilter = params.pieceColor || "both";
        this.matchTypes = PieceSetupEngine.parseMatchPieceTypes(params.matchPieceTypes);
    }
    get targetPieces() {
        return this.state?.targetPieces || [];
    }
    initializeState() {
        const params = this.config.params || {};
        const playerColor = params.pieceColor || "both";
        const matchTypes = PieceSetupEngine.parseMatchPieceTypes(params.matchPieceTypes);
        this.playerColorFilter = playerColor;
        this.matchTypes = matchTypes;
        // Target position the player must achieve
        const targetFen = params.targetFen || CHESS_START_POSITION;
        const targetBoard = BoardUtils.parseFenToBoard(targetFen);
        const parsedTargetPieces = targetBoard?.pieces || [];
        const targetPiecesToPlace = this.filterTargetPieces(parsedTargetPieces, playerColor, matchTypes);
        // Starting position: FEN from config (PGN [FEN]) or params.startFen; default empty
        const startFen = params.startFen || this.config.fen || CHESS_EMPTY_BOARD_FEN;
        const startBoard = BoardUtils.parseFenToBoard(startFen);
        this.pieces = startBoard?.pieces || [];
        const { correctSquares, incorrectSquares } = this.calculateSquareStatus(parsedTargetPieces);
        return {
            fen: startFen,
            moves: [],
            moveCount: 0,
            targetsAchieved: correctSquares.length,
            totalTargets: targetPiecesToPlace.length,
            remainingTargets: [],
            foundSquares: [],
            wrongSquares: [],
            status: "playing",
            selectedPiece: null,
            targetFen,
            targetPieces: parsedTargetPieces,
            correctSquares,
            incorrectSquares,
        };
    }
    calculateSquareStatus(targetPieces) {
        const targets = targetPieces || this.targetPieces;
        const correctSquares = [];
        const incorrectSquares = [];
        for (const piece of this.pieces) {
            const targetPiece = targets.find(t => t.square === piece.square);
            if (targetPiece && targetPiece.type === piece.type && targetPiece.color === piece.color) {
                correctSquares.push(piece.square);
            }
            else {
                incorrectSquares.push(piece.square);
            }
        }
        return { correctSquares, incorrectSquares };
    }
    getValidMoves(_square) {
        return [];
    }
    makeMove(from, to) {
        if (from === to) {
            return this.handleClick(to);
        }
        return this.handleDrag(from, to);
    }
    handleDrag(from, to) {
        if (this.state.status !== "playing") {
            return { valid: false, error: "Game is already over" };
        }
        const pieceIndex = this.pieces.findIndex(p => p.square === from);
        if (pieceIndex === -1) {
            return { valid: false, error: "No piece to move" };
        }
        const movingPiece = this.pieces[pieceIndex];
        const destPieceIndex = this.pieces.findIndex(p => p.square === to);
        // Single-pass: move dragged piece to dest, swap dest piece to origin if occupied
        this.pieces = this.pieces.map((p, i) => {
            if (i === pieceIndex)
                return { ...movingPiece, square: to };
            if (destPieceIndex !== -1 && i === destPieceIndex)
                return { ...p, square: from };
            return p;
        });
        this.state.moveCount++;
        this.state.moves.push({ from, to });
        return this.updateStateAfterChange();
    }
    handleClick(square) {
        if (this.state.status !== "playing") {
            return { valid: false, error: "Game is already over" };
        }
        const selectedPiece = this.state.selectedPiece;
        if (!selectedPiece) {
            // Click on existing piece removes it
            const existingPiece = this.pieces.find(p => p.square === square);
            if (existingPiece) {
                return this.removePiece(square);
            }
            return { valid: false, error: "Select a piece type first" };
        }
        const existingPiece = this.pieces.find(p => p.square === square);
        if (existingPiece) {
            // Same piece type: remove it; different: replace it
            if (existingPiece.type === selectedPiece.type && existingPiece.color === selectedPiece.color) {
                return this.removePiece(square);
            }
            return this.replacePiece(square, selectedPiece.type, selectedPiece.color);
        }
        return this.placePiece(square, selectedPiece.type, selectedPiece.color);
    }
    placePiece(square, type, color) {
        this.pieces = [...this.pieces, { type, color, square }];
        this.state.moveCount++;
        this.state.moves.push({ from: square, to: square });
        return this.updateStateAfterChange();
    }
    removePiece(square) {
        if (!this.pieces.some(p => p.square === square)) {
            return { valid: false, error: "No piece to remove" };
        }
        this.pieces = this.pieces.filter(p => p.square !== square);
        this.state.moveCount++;
        this.state.moves.push({ from: square, to: square });
        return this.updateStateAfterChange();
    }
    /**
     * Replace a piece on the board (click-to-place or sidebar drop path).
     * Simply overwrites whatever is on the square with the new piece.
     * Swapping is only handled by handleDrag (board-to-board moves).
     */
    replacePiece(square, newType, newColor) {
        const pieceIndex = this.pieces.findIndex(p => p.square === square);
        if (pieceIndex === -1) {
            return { valid: false, error: "No piece to replace" };
        }
        this.pieces = this.pieces.map(p => p.square === square ? { type: newType, color: newColor, square } : p);
        this.state.moveCount++;
        this.state.moves.push({ from: square, to: square });
        return this.updateStateAfterChange();
    }
    updateStateAfterChange() {
        this.updateFen();
        const { correctSquares, incorrectSquares } = this.calculateSquareStatus();
        this.state.correctSquares = correctSquares;
        this.state.incorrectSquares = incorrectSquares;
        this.state.targetsAchieved = correctSquares.length;
        if (this.checkWinCondition()) {
            this.state.status = "complete";
            return { valid: true, newFen: this.state.fen, gameComplete: true };
        }
        if (this.isMoveLimitExceeded()) {
            this.state.status = "failed";
            return { valid: true, newFen: this.state.fen, gameFailed: true, error: "Move limit exceeded!" };
        }
        return { valid: true, newFen: this.state.fen, targetHit: true };
    }
    filterTargetPieces(pieces, playerColor, matchTypes) {
        let filtered = playerColor === "both" ? pieces : pieces.filter(p => p.color === playerColor);
        if (matchTypes.size > 0) {
            filtered = filtered.filter(p => matchTypes.has(p.type));
        }
        return filtered;
    }
    static parseMatchPieceTypes(value) {
        if (!value || value === "" || value.toLowerCase() === "all") {
            return new Set();
        }
        const validTypes = ["k", "q", "r", "b", "n", "p"];
        const parts = value.toLowerCase().split(",").map(s => s.trim());
        const set = new Set();
        for (const p of parts) {
            if (validTypes.includes(p))
                set.add(p);
        }
        return set;
    }
    checkWinCondition() {
        const targetPiecesToCheck = this.filterTargetPieces(this.targetPieces, this.playerColorFilter, this.matchTypes);
        // All required target pieces must be in correct position
        for (const targetPiece of targetPiecesToCheck) {
            const currentPiece = this.pieces.find(p => p.square === targetPiece.square);
            if (!currentPiece || currentPiece.type !== targetPiece.type || currentPiece.color !== targetPiece.color) {
                return false;
            }
        }
        // No extra pieces allowed (of the matched types/colors)
        const targetSquares = new Set(targetPiecesToCheck.map(p => p.square));
        const currentPiecesToCheck = this.filterTargetPieces(this.pieces, this.playerColorFilter, this.matchTypes);
        for (const piece of currentPiecesToCheck) {
            if (!targetSquares.has(piece.square)) {
                return false;
            }
        }
        return true;
    }
    selectPiece(type, color) {
        if (this.state) {
            this.state.selectedPiece = { type, color };
        }
    }
    clearSelection() {
        if (this.state) {
            this.state.selectedPiece = null;
        }
    }
    addPieceAt(square, type, color) {
        if (this.state.status !== "playing") {
            return { valid: false, error: "Game is already over" };
        }
        const existingPiece = this.pieces.find(p => p.square === square);
        if (existingPiece) {
            return this.replacePiece(square, type, color);
        }
        return this.placePiece(square, type, color);
    }
    getHint() {
        if (this.state.status !== "playing")
            return null;
        const targetPieces = this.filterTargetPieces(this.targetPieces, this.playerColorFilter, this.matchTypes);
        for (const targetPiece of targetPieces) {
            const currentPiece = this.pieces.find(p => p.square === targetPiece.square);
            if (!currentPiece || currentPiece.type !== targetPiece.type || currentPiece.color !== targetPiece.color) {
                return { from: targetPiece.square, to: targetPiece.square };
            }
        }
        return null;
    }
    getState() {
        return {
            ...this.state,
            moves: [...this.state.moves],
            remainingTargets: [...this.state.remainingTargets],
            foundSquares: [...this.state.foundSquares],
            wrongSquares: [...this.state.wrongSquares],
            targetPieces: [...this.state.targetPieces],
            correctSquares: [...this.state.correctSquares],
            incorrectSquares: [...this.state.incorrectSquares],
        };
    }
}
// ========================================
// Registration
// ========================================
MiniGameRegistry.register({
    type: "PIECE_SETUP",
    createEngine: (config) => new PieceSetupEngine(config),
    validate: (config) => {
        const params = config.params || {};
        const pieceColor = params.pieceColor;
        if (pieceColor && !["w", "b", "both"].includes(pieceColor)) {
            return { valid: false, errors: [`Invalid pieceColor "${pieceColor}". Must be "w", "b", or "both".`] };
        }
        const matchPieceTypes = params.matchPieceTypes;
        if (matchPieceTypes && matchPieceTypes.toLowerCase() !== "all") {
            const validTypes = ["k", "q", "r", "b", "n", "p"];
            const parts = matchPieceTypes.toLowerCase().split(",").map(s => s.trim());
            for (const p of parts) {
                if (p && !validTypes.includes(p)) {
                    return { valid: false, errors: [`Invalid matchPieceTypes "${matchPieceTypes}". Use k,q,r,b,n,p or "all".`] };
                }
            }
        }
        return { valid: true, errors: [] };
    },
    getInstructionText: (config) => {
        const params = config.params || {};
        const pieceColor = params.pieceColor || "both";
        let colorText = "";
        if (pieceColor === "w")
            colorText = "white ";
        else if (pieceColor === "b")
            colorText = "black ";
        return `Set up the ${colorText}pieces to match the target position.`;
    },
    supportsHints: true,
    interactionType: "click",
    uiConfig: {
        editableBoard: true,
    },
});
